import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SerializeFrom } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import {
    CONVERSATIONS_API,
    type loader,
} from '~/routes/remix-api.chat.conversations';
import type { Conversation } from '../types';
import { useConversationCache } from './use-conversation-cache';

type ConversationsResult = NonNullable<SerializeFrom<typeof loader>>;
type PageInfo = ConversationsResult['pageInfo'];

/**
 * Hook to get a paginated list of conversations for the current user.
 * @param limit The maximum number of conversations to return. Do not change this
 *  value. Defaults to 20.
 */
export function useConversations({
    limit = 20,
}: {
    limit?: number;
} = {}) {
    const { lastCachedConversation } = useConversationCache();
    const isFetched = useRef(false);
    const [search, setSearch] = useState('');
    const [buildHubIDs, setBuildHubIDs] = useState<string[]>([]);
    const [offset, setOffset] = useState(0);
    const [conversations, setConversations] = useState<Conversation[] | null>(
        null
    );
    const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
    const fetcher = useFetcher<typeof loader>();
    const apiUrl = useMemo(() => {
        const url = new URL(CONVERSATIONS_API, window.location.origin);
        url.searchParams.set('q', search);
        url.searchParams.set('limit', String(limit));
        url.searchParams.set('offset', String(offset));
        for (const buildHubID of buildHubIDs) {
            url.searchParams.append('buildHubIDs', buildHubID);
        }
        return `${url.pathname}${url.search}`;
    }, [search, buildHubIDs, limit, offset]);

    const setParams = useCallback(
        (params: { search: string; buildHubIDs: string[] }) => {
            setSearch(params.search.trim());
            setBuildHubIDs(params.buildHubIDs);
            setOffset(0);
        },
        []
    );

    const loadMore = useCallback(() => {
        if (
            fetcher.state !== 'idle' ||
            !conversations ||
            !pageInfo?.hasNextPage
        ) {
            return;
        }
        setOffset(conversations.length);
    }, [fetcher.state, conversations, pageInfo]);

    useEffect(() => {
        // Fetch initial conversations on mount.
        if (fetcher.state === 'idle' && !isFetched.current) {
            fetcher.load(apiUrl);
            isFetched.current = true;
        } else if (isFetched.current) {
            // Refetch when the query params change.
            fetcher.load(apiUrl);
        }
        // Must not include `fetcher` to avoid infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl]);

    // Set data from the fetcher.
    useEffect(() => {
        if (!fetcher.data || fetcher.state !== 'idle') {
            return;
        }
        const newConversations = fetcher.data.conversations;
        const newPageInfo = fetcher.data.pageInfo;
        setConversations((prev) => {
            if (!prev || !newPageInfo.hasPreviousPage) {
                return newConversations;
            }
            const next = [...prev];
            for (const conversation of newConversations) {
                if (!prev.some((c) => c.ID === conversation.ID)) {
                    next.push(conversation);
                }
            }
            return next;
        });
        setPageInfo(newPageInfo);
    }, [fetcher]);

    // Update the chat settings and unread message count as soon as possible
    // from the conversation cache.
    useEffect(() => {
        if (!lastCachedConversation) return;
        setConversations((prev) => {
            if (prev?.some((c) => c.ID === String(lastCachedConversation.ID))) {
                return prev.map((c) =>
                    c.ID === String(lastCachedConversation.ID)
                        ? {
                              ...c,
                              UnreadMessageCount:
                                  lastCachedConversation.UnreadMessageCount,
                              Unread: lastCachedConversation.Unread,
                              ...(lastCachedConversation.__typename ===
                              'DirectConversation'
                                  ? { Name: lastCachedConversation.Name }
                                  : {}),
                          }
                        : c
                );
            }
            return prev;
        });
    }, [lastCachedConversation]);

    return {
        /**
         * The list of conversations.
         */
        conversations,
        /**
         * There are more conversations to load.
         */
        hasMore: pageInfo?.hasNextPage ?? false,
        /**
         * The conversations are currently being loaded.
         */
        isLoading: fetcher.state !== 'idle',
        /**
         * Set the filter parameters for the `GetConversations` query.
         */
        setParams,
        /**
         * Refetch the conversations.
         */
        refetch: useCallback(() => {
            setOffset(0);
            fetcher.load(apiUrl);
        }, [fetcher, apiUrl]),
        /**
         * Load more conversations by fetching the next page. This increments the
         * offset and appends the new results to the existing conversations.
         */
        loadMore,
    };
}
